/**
 * Where should we drop a user after login / signup, when there's no
 * explicit `from` URL to honor?
 *
 * Priority:
 *   1. First org the user is part of (owned wins over joined) →
 *      OrganizationHomePage for that org.
 *   2. publicData.role === 'creator' → CreatorHomePage.
 *   3. null (caller falls back to LandingPage / CommunityPage / etc.)
 *
 * Routing detail: returns a route name + params, not a path string, so
 * the caller can use NamedRedirect (preferred) and route-name renames
 * stay greppable from one place.
 */

import config from '../config';
import { getAllUserOrgs } from './orgMembership';

/**
 * @param {object} user — currentUser entity (after ensureCurrentUser).
 * @returns {{ name: string, params?: object } | null}
 */
export const getPostAuthRedirectRoute = user => {
  if (!user) return null;

  // 1) Any org the user is in — getAllUserOrgs returns owned first, then
  //    joined (admin/manager/member). The first entry is the "best" one
  //    to land on; in practice users typically only have one org anyway.
  const orgs = getAllUserOrgs(user);
  if (orgs && orgs.length > 0) {
    return {
      name: 'OrganizationHomePage',
      params: { orgId: orgs[0].id },
    };
  }

  // 2) Plain creator.
  const profileRole = user?.attributes?.profile?.publicData?.role;
  if (profileRole === config.userRoles.creator) {
    return { name: 'CreatorHomePage' };
  }

  return null;
};
